import { Link } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber } from '@/lib/decimal'
import { formatEuros, formatPercent } from '@/lib/format'
import type { Invoice } from '@/lib/invoice-types'
import { waitingLineOf, waitingOn } from '@/lib/payout-waiting'
import { cn } from '@/lib/utils'

const MAX_ROWS = 5
const SHOWN_WHEN_TRUNCATED = 4

/**
 * Wie wat krijgt, als voetnoot bij het bedrag in het zijpaneel. Alleen de
 * uitkomst: geen totaalregel (de verdeling sommeert per definitie op het
 * subtotaal erboven), geen wachtzinnen, geen acties. Het blok is zelf de
 * doorgang naar het tabblad Verdeling, waar wel gehandeld kan worden.
 *
 * Nulregels blijven hier weg. Een ontvanger die deze keer niets krijgt staat
 * wel in het verdeel-script en dus op het tabblad, maar in een snelblik voegt
 * "€ 0,00" niets toe; de overgebleven regels sommeren nog steeds op het
 * subtotaal.
 */
export function DistributionSummary({ invoice, onNavigate }: { invoice: Invoice; onNavigate: () => void }) {
  const lines = invoice.distribution.filter((line) => toNumber(line.amount) !== 0)
  if (lines.length === 0) {
    return null
  }

  const truncated = lines.length > MAX_ROWS
  const shown = truncated ? lines.slice(0, SHOWN_WHEN_TRUNCATED) : lines
  const rest = lines.length - shown.length
  const subtotal = toNumber(invoice.subtotal)

  return (
    <Link
      to="/facturen/$id"
      params={{ id: invoice.id }}
      search={{ tab: 'distribution' }}
      onClick={onNavigate}
      className="group -mx-2 mt-3 block rounded-lg border-t border-hairline px-2 pb-1.5 pt-3 transition-colors hover:bg-fill-1"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="label-caps">Verdeling</span>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-faint transition-opacity md:opacity-0 md:group-hover:opacity-100" />
      </div>

      <div className="mt-1.5 space-y-1">
        {shown.map((line) => {
          const waiting = waitingOn(waitingLineOf(invoice, line))
          const attention = waiting.tone === 'attention'
          return (
            <div key={line.id} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-baseline gap-1.5">
                <Tooltip content={attention ? waiting.label : null}>
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 -translate-y-px rounded-full',
                      attention ? waiting.dot : 'bg-transparent',
                    )}
                  />
                </Tooltip>
                <span className="truncate text-ink-soft">{line.beneficiaryLabel}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-1.5">
                {subtotal !== 0 ? (
                  <span className="text-xs tabular-nums text-ink-faint">
                    {formatPercent(toNumber(line.amount) / subtotal)}
                  </span>
                ) : null}
                <span className="money text-ink">{formatEuros(toNumber(line.amount))}</span>
              </span>
            </div>
          )
        })}
        {rest > 0 ? (
          <p className="text-sm text-ink-faint">nog {rest === 1 ? '1 ontvanger' : `${rest} ontvangers`}</p>
        ) : null}
      </div>
    </Link>
  )
}
